import {
  CSSProperties,
  ChangeEvent,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useErrorBoundary } from "react-error-boundary";

import { logger } from "c7-logger/index";

import { InputTitle, ThemeContext } from "chameleon/lib/components";
import HelpTooltip from "chameleon/lib/components/help-tooltip/help-tooltip";

import { ChexFieldFrame } from "checkout-experience/components/field-frame/chex-field-frame";
import { frameInputWrapper } from "checkout-experience/components/field-frame/wrapper.css";

import { sendCustomAction } from "datadog-loader/loader";

import { ExternalFieldFrameProps } from "field-frames/src/frame/types";
import { getInternalName } from "field-frames/src/utils/internal-name";

export const FieldFrameJSX = forwardRef<
  HTMLDivElement,
  ExternalFieldFrameProps
>((props: ExternalFieldFrameProps, forwardedRef) => {
  const { showBoundary } = useErrorBoundary();
  const internalName = useRef(getInternalName());
  const fieldFrameRef = useRef<ChexFieldFrame>();
  const theme = useContext(ThemeContext);
  const [fieldState, setFieldState] = useState({
    hasFocus: false,
    hasBlurred: false,
  });
  const ref = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, () => ref.current as HTMLInputElement);

  useEffect(() => {
    if (ref && ref.current) {
      const focusOnField = () => {
        fieldFrameRef?.current?.sendFocusMessage();
      };

      ref.current.addEventListener("focus", focusOnField);
      return () => {
        ref.current?.removeEventListener("focus", focusOnField);
      };
    }
    return () => {};
  }, [ref]);

  useEffect(() => {
    if (fieldFrameRef?.current?.shiftTabPressed) {
      if (fieldFrameRef?.current?.root?.parentElement) {
        const focusEls: Array<HTMLElement> = Array.from(
          document.querySelectorAll("input:not([type='radio']), [tabindex='0']")
        );
        const selfIndex = focusEls.indexOf(
          fieldFrameRef?.current?.root.parentElement
        );
        if (selfIndex > 0) {
          focusEls[selfIndex - 1].focus?.();
        }
      }
    }
  }, [fieldFrameRef?.current?.shiftTabPressed]);

  useEffect(() => {
    fieldFrameRef?.current?.updateShowCheckmarkOrSpinner({
      showSpinner: props.showSpinner ?? false,
      showCheckmark: props.showCheckmark ?? false,
    });
  }, [props.showSpinner, props.showCheckmark]);

  useEffect(() => {
    fieldFrameRef?.current?.updateFetchingCardType(props.fetchingCardType);
  }, [props.fetchingCardType]);

  // This trips when the window.crypto library is broken/undefined inside the field frame
  const criticalErrorHasOccurred = () => {
    showBoundary("Encryption is not functioning correctly");
  };

  const onError = (errors: string[]) => {
    // propagate
    props.onError?.(errors);
  };

  const onBlur = () => {
    setFieldState((prevState) => ({
      ...prevState,
      hasFocus: false,
      hasBlurred: true,
    }));
    props.onBlur?.();
  };

  const onFocus = () => {
    setFieldState((prevState) => ({
      ...prevState,
      hasFocus: true,
    }));
    props.onFocus?.();
  };

  useEffect(() => {
    props.isLoading?.();

    if (fieldFrameRef.current) {
      fieldFrameRef.current.destroy();
      logger.error("Field frame encryption key changed after rendered");
      sendCustomAction({ name: "Encryption Key Changed", context: {} });
      props.onChange({
        target: { value: "" },
      } as ChangeEvent<HTMLInputElement>);
      props.onError?.([]);
      setFieldState({ hasFocus: false, hasBlurred: false });
    }

    fieldFrameRef.current = new ChexFieldFrame({
      ...props,
      onError,
      onBlur,
      onFocus,
      internalName: internalName.current,
      themeValue: theme,
      isReady: props.isReady,
      criticalErrorHasOccurred,
    });
  }, [props.encryptionKey, JSON.stringify(theme)]);

  const cssProps: CSSProperties = {};
  cssProps.width = "100%";
  if (fieldState.hasFocus) {
    cssProps.borderRadius = theme.borderRadius.form;
    cssProps.outline = `${theme.outline.standard} dotted ${theme.color.outline}`;
    cssProps.outlineOffset = theme.outline.offset;
  }

  // set height manually
  return (
    <>
      <InputTitle htmlFor={internalName.current} title={props.title} />
      <div
        className={frameInputWrapper}
        ref={ref}
        // Tab index is needed for focus event listener on ref
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
      >
        <div
          style={cssProps}
          id={internalName.current}
          data-testid={internalName.current}
        >
          <div
            style={{
              width: "100%",
              height: "50px",
            }}
          ></div>
        </div>
        {props.id === "securityCode" && (
          <HelpTooltip content={props.translatedValues?.tooltipContent} />
        )}
      </div>
    </>
  );
});

FieldFrameJSX.displayName = "FieldFrameJSX";
